import AppSettings from "Settings/AppSettings";
import AuthManager from "Common/AuthManager";
import ServiceWrapper from "Services/ServiceWrapper";
import PermissionsManager from "Common/PermissionsManager";

// Mirrors useSearchBar.tsx's readWorkspaceKey/writeWorkspaceKey exactly — workspace selection is
// per-tab (sessionStorage, seeded from localStorage). Duplicated rather than imported because this
// module must stay hook-free and loader-safe; useSearchBar.tsx's copies are for the (untouched)
// manual-switch family.
const readWorkspaceKey = (key: string): string | null =>
    sessionStorage.getItem(key) ?? localStorage.getItem(key);

const writeWorkspaceKey = (key: string, value: string) => {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
};

const sortByName = (list: any[]): any[] =>
    [...list].sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()));

// Routes that only exist to report "workspace not usable (yet)". A successful bootstrap or
// workspace switch must never leave the user parked on one of these: they carry no content of
// their own, so re-rendering one after success shows a stale error over a healthy session.
// (MenuItem.tsx keeps its own narrower list — it controls sidebar interactivity, where
// /workspace/module/permission/denied deliberately still allows navigating away.)
export const WORKSPACE_NOT_READY_ROUTES: readonly string[] = [
    "/workspace/preparing",
    "/workspace/notfound",
    "/workspace/industry/notfound",
    "/workspace/module/permission/denied",
];

export const isWorkspaceNotReadyRoute = (pathname: string): boolean =>
    WORKSPACE_NOT_READY_ROUTES.includes(pathname);

// Why a candidate party was passed over during bootstrap — the step of the chain that came back
// empty or failed for it. Surfaced to the user by useSearchBar.tsx so a silent switch to a
// different party is never a mystery.
export type SkippedPartyReason = "industry" | "parent_channel" | "environment" | "switch";
export type SkippedParty = { party: any; reason: SkippedPartyReason };

export type WorkspaceBootstrapResult = {
    workspaceLoaded: boolean;
    redirectTo: string | null;
    // Parties tried before the one that loaded (or before giving up), in the order attempted.
    skippedParties: SkippedParty[];
    party: any | null;
    partyList: any[];
    industry: any | null;
    industryList: any[];
    parentChannel: any | null;
    parentChannelList: any[];
    environment: any | null;
    environmentList: any[];
};

const NO_WORKSPACE: WorkspaceBootstrapResult = {
    workspaceLoaded: false,
    redirectTo: null,
    skippedParties: [],
    party: null,
    partyList: [],
    industry: null,
    industryList: [],
    parentChannel: null,
    parentChannelList: [],
    environment: null,
    environmentList: [],
};

// The one-time party -> industry -> parent channel -> environment -> party/switch chain that used
// to live in useSearchBar.tsx's refreshAll/refreshDomains/refreshChannels/refreshEnvironments,
// re-implemented as a plain, hook-free async function so it can run in a router loader (loaders
// have no access to useContext/useNavigate). refreshDomains/refreshChannels/refreshEnvironments
// themselves are untouched in useSearchBar.tsx — they're still used by refreshParties, which 16
// admin party/domain/channel management screens call directly; duplicating the sequence here avoids
// touching that shared code at all.
//
// Party fallback: the chain is attempted per party, in candidate order (the remembered party
// first, then the backend's order), and the first party whose whole chain succeeds — including
// party/switch — is the one loaded. Before this, the single default party was tried and any
// failure sent the user to /workspace/module/permission/denied even when every other party they
// belong to was perfectly usable (prod 2026-09-01: a user's oldest party had no usable workspace,
// so login failed for them until that party was removed from their SAMS access). Permission-denied
// is now only the outcome when NO party has a complete workspace.

type PartyWorkspace = {
    industry: any;
    industryList: any[];
    parentChannel: any;
    parentChannelList: any[];
    environment: any;
    environmentList: any[];
};

const EMPTY_WORKSPACE: PartyWorkspace = {
    industry: null,
    industryList: [],
    parentChannel: null,
    parentChannelList: [],
    environment: null,
    environmentList: [],
};

type StoredSelection = {
    industryId: string | null;
    parentChannelId: string | null;
    environmentId: string | null;
};

type PartyAttempt =
    | { ok: true; workspace: PartyWorkspace }
    | { ok: false; reason: SkippedPartyReason; partial: PartyWorkspace };

// Industry -> parent channel -> environment for one party. Returns the partial picture on failure
// so the permission-denied page (and its Searchbar) can still show whatever levels did resolve.
async function resolvePartyWorkspace(
    api_base_url: string,
    token: string | null,
    party: any,
    stored: StoredSelection
): Promise<PartyAttempt> {
    const partial: PartyWorkspace = { ...EMPTY_WORKSPACE };

    const industryResp = await ServiceWrapper.doGet(
        api_base_url + "party/" + party.id + "/industry/list/active",
        null,
        null,
        token
    );
    if (industryResp.errorfound || !industryResp.data?.data || industryResp.data.data.length === 0) {
        return { ok: false, reason: "industry", partial };
    }
    const rawIndustryList = industryResp.data.data;
    partial.industryList = sortByName(rawIndustryList);
    // Default pick from the raw list, sorted copy only for display — see the party pick in runBootstrap.
    partial.industry = rawIndustryList[0];
    if (stored.industryId) {
        const found = partial.industryList.find((i: any) => i.id === stored.industryId);
        if (found) partial.industry = found;
    }

    const parentResp = await ServiceWrapper.doGet(
        `${api_base_url}list/party/${party.id}/industry/${partial.industry.id}/parent_channel`,
        null,
        null,
        token
    );
    const parentData = parentResp?.data;
    if (!parentData?.data || !Array.isArray(parentData.data) || parentData.data.length === 0) {
        return { ok: false, reason: "parent_channel", partial };
    }
    partial.parentChannelList = sortByName(parentData.data);
    partial.parentChannel = parentData.data[0];
    if (stored.parentChannelId) {
        const found = partial.parentChannelList.find((c: any) => c.id === stored.parentChannelId);
        if (found) partial.parentChannel = found;
    }

    const envResp = await ServiceWrapper.doGet(
        `${api_base_url}list/party/${party.id}/parent_channel/${partial.parentChannel.id}/channel`,
        null,
        null,
        token
    );
    const envData = envResp?.data;
    if (!envData?.data || !Array.isArray(envData.data) || envData.data.length === 0) {
        return { ok: false, reason: "environment", partial };
    }
    partial.environmentList = sortByName(envData.data);
    partial.environment = envData.data[0];
    if (stored.environmentId) {
        const found = partial.environmentList.find((e: any) => e.id === stored.environmentId);
        if (found) partial.environment = found;
    }

    return { ok: true, workspace: partial };
}

// Remembered party first (if it is still in the list), then the backend's order. The backend
// returns each user's parties oldest-first, and that raw order — not the alphabetical one used for
// display — is what the default pick has always followed (see the a84ef8c80 note that restoring
// it fixed the default landing on the Compatio workspace).
function orderCandidateParties(rawPartyList: any[], storedPartyId: string | null): any[] {
    if (!storedPartyId) return rawPartyList;
    const stored = rawPartyList.find((p: any) => p.id === storedPartyId);
    if (!stored) return rawPartyList;
    return [stored, ...rawPartyList.filter((p: any) => p.id !== stored.id)];
}

async function runBootstrap(): Promise<WorkspaceBootstrapResult> {
    const api_base_url = AppSettings.GMS_BACKEND_API_URL;
    const token = AuthManager.getGmsToken();

    const stored: StoredSelection = { industryId: null, parentChannelId: null, environmentId: null };
    const storedChannelStr = readWorkspaceKey("selectedParentChannel");
    if (storedChannelStr) {
        try {
            stored.parentChannelId = JSON.parse(storedChannelStr).id;
        } catch (e) {
            console.error("[workspaceBootstrap] Error parsing stored channel:", e);
        }
    }
    const storedEnvStr = readWorkspaceKey("selectedEnvironment");
    if (storedEnvStr) {
        try {
            stored.environmentId = JSON.parse(storedEnvStr).id;
        } catch (e) {
            console.error("[workspaceBootstrap] Error parsing stored environment:", e);
        }
    }
    const storedIndustry = localStorage.getItem("industry");
    if (storedIndustry) {
        try {
            stored.industryId = JSON.parse(storedIndustry.replace(/\\/g, "")).id;
        } catch (e) {
            console.error("[workspaceBootstrap] Invalid industry JSON in localStorage:", e);
        }
    }

    const partiesResp = await ServiceWrapper.doGet(api_base_url + "user/party/active/list", null, null, token);
    if (partiesResp.errorfound || !partiesResp.data?.data || partiesResp.data.data.length <= 0) {
        return NO_WORKSPACE;
    }
    const rawPartyList = partiesResp.data.data;
    const partyList = sortByName(rawPartyList);
    const candidates = orderCandidateParties(rawPartyList, localStorage.getItem("party"));

    const skippedParties: SkippedParty[] = [];
    let lastTried: { party: any; partial: PartyWorkspace } | null = null;

    for (const party of candidates) {
        const attempt = await resolvePartyWorkspace(api_base_url, token, party, stored);
        if (!attempt.ok) {
            skippedParties.push({ party, reason: attempt.reason });
            lastTried = { party, partial: attempt.partial };
            continue;
        }
        const ws = attempt.workspace;

        // Core of switchWorkspace(), minus the parts that need hooks (reading component state,
        // appData.update_party, navigate on failure) — those are applied by the caller instead.
        // switchWorkspace() itself is untouched; the manual-switch handlers still call it directly.
        const psresp = await ServiceWrapper.doPost(
            api_base_url + "party/switch",
            {
                party_id: party.id,
                industry_id: ws.industry.id,
                app_name: "GMS",
                parent_channel_id: ws.parentChannel.id,
                channel_id: ws.environment.id,
            } as any,
            null,
            token
        );
        if (psresp.errorfound || !psresp.data?.done) {
            skippedParties.push({ party, reason: "switch" });
            lastTried = { party, partial: ws };
            continue;
        }

        // Persist selections only once the switch has gone through — same keys/order as
        // refreshDomains/refreshChannels/refreshEnvironments. A party that failed part-way must not
        // become the remembered one, or the next reload would try it first again.
        localStorage.setItem("party", party.id);
        localStorage.setItem("industry", JSON.stringify(ws.industry));
        writeWorkspaceKey("selectedParentChannel", JSON.stringify(ws.parentChannel));
        writeWorkspaceKey("selectedEnvironment", JSON.stringify(ws.environment));

        localStorage.setItem("parties", JSON.stringify(psresp.data.user_object?.profile?.parties || ""));
        localStorage.setItem("current_party_id", JSON.stringify(psresp.data.user_object?.current_party_id || ""));

        await AuthManager.setGmsToken(psresp.data.access_token);
        if (psresp.data.user_object) {
            await AuthManager.setAuthUser(psresp.data.user_object);
        }
        await new PermissionsManager().ProcessPermissions();

        return {
            workspaceLoaded: true,
            redirectTo: null,
            skippedParties,
            party,
            partyList,
            ...ws,
        };
    }

    // Every party the user belongs to failed somewhere in the chain. Report the last one tried with
    // whatever levels did resolve for it, so the permission-denied page can still show them.
    return {
        ...NO_WORKSPACE,
        redirectTo: "/workspace/module/permission/denied",
        skippedParties,
        party: lastTried?.party ?? null,
        partyList,
        ...(lastTried?.partial ?? EMPTY_WORKSPACE),
    };
}

// The actual fix: memoized at module scope, not tied to any component's lifetime. Calling this
// any number of times, from any component that mounts and unmounts freely, runs the real work
// exactly once — the guarantee comes from the module, not from any component never remounting.
let bootstrapPromise: Promise<WorkspaceBootstrapResult> | null = null;

export function getWorkspaceBootstrap(): Promise<WorkspaceBootstrapResult> {
    if (!bootstrapPromise) {
        bootstrapPromise = runBootstrap();
    }
    return bootstrapPromise;
}
